import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { pontoApi, type UsuarioLookup } from "@/features/ponto/pontoApi";

interface UsuarioSearchComboboxProps {
  /** Id do usuário (0/undefined = nenhum). */
  value?: number;
  onChange: (id: number, usuario?: UsuarioLookup) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Seleção de usuário de login da empresa (nome + login), com busca no servidor por nome,
 * login ou e-mail. Usa o lookup do RH (`/api/rh/ponto/usuarios-lookup`), que não exige a
 * permissão do cadastro de usuários. O id interno nunca aparece.
 */
export const UsuarioSearchCombobox = ({
  value,
  onChange,
  placeholder = "Sem usuário vinculado",
  disabled,
  className,
}: UsuarioSearchComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const busca = useDebounce(search, 250);

  const { data: resultados = [], isFetching } = useQuery({
    queryKey: ["usuarios-lookup", busca],
    queryFn: () => pontoApi.usuarios(busca),
    enabled: open,
    staleTime: 15_000,
  });

  const { data: selecionado } = useQuery({
    queryKey: ["usuario-lookup", value],
    queryFn: async () => (await pontoApi.usuarios(undefined, [value!]))[0] ?? null,
    enabled: !!value,
    staleTime: 60_000,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn("relative w-full", className)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled}
            type="button"
          >
            <span className={cn("truncate", !selecionado && "text-muted-foreground")}>
              {value && selecionado ? `${selecionado.nome} (${selecionado.login})` : value ? "Carregando…" : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {!!value && !disabled && (
          <button
            type="button"
            aria-label="Remover vínculo"
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); onChange(0); }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-[380px] max-w-[calc(100vw-2rem)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar por nome, login ou e-mail..." value={search} onValueChange={setSearch} />
          <CommandList>
            {isFetching && resultados.length === 0 && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isFetching && <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>}
            <CommandGroup>
              {resultados.map((u) => (
                <CommandItem key={u.id} value={String(u.id)} onSelect={() => { onChange(u.id, u); setOpen(false); }}>
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === u.id ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{u.nome}</span>
                    <span className="text-xs text-muted-foreground truncate">{u.login}{u.email ? ` · ${u.email}` : ""}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
